import type {
  ITicketConfigTour,
  TicketConfigTourPayload,
} from '@/shared/api/types/ticket-config-types'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTicketFeaturesQuery } from '@/entities/ticket-feature'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import { Button } from '@/shared/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { TourFeaturesRow } from './tour-features-row'
import { useTicketConfigTours } from './use-ticket-config-tours'

interface TicketConfigToursCardProps {
  tours: ITicketConfigTour[]
  /** Saved config-level feature ids: what a tour without its own set uses. */
  defaultFeatureIds: string[]
  submitting: boolean
  onSave: (tours: TicketConfigTourPayload[]) => Promise<void> | void
}

/** Mount with a `key` that changes with the server data: the draft state starts from `tours`. */
export function TicketConfigToursCard({
  tours,
  defaultFeatureIds,
  submitting,
  onSave,
}: TicketConfigToursCardProps) {
  const { t } = useTranslation()
  const { activeLanguage } = useDashboardDisplayLanguage()
  const { data } = useTicketFeaturesQuery({ current: 1, pageSize: 100 })
  const { selections, isDirty, payload, setFeatureChecked, resetTourToDefault, resetAll } =
    useTicketConfigTours({ tours, defaultFeatureIds })

  const features = data?.data.data ?? []

  return (
    <Card className="bg-white rounded-2xl border border-zinc-200">
      <CardHeader>
        <CardTitle className="text-base">
          {t('ticketConfig.steps.tours', { defaultValue: 'Tours' })}
        </CardTitle>
        <CardDescription>
          {t('ticketConfig.toursSubtitle', {
            defaultValue:
              'Tours use the config features above unless you set their own. New tickets copy these per-tour features when purchased.',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {tours.length === 0 ? (
          <EmptyState
            title={t('ticketConfig.noTours', { defaultValue: 'No tours found' })}
            description={t('ticketConfig.noToursHint', {
              defaultValue: 'Bind destinations that have tours to this config first.',
            })}
          />
        ) : (
          tours.map((tour) => (
            <TourFeaturesRow
              key={tour.tourId}
              tourId={tour.tourId}
              tourName={tour.name?.trim() ?? tour.tourId}
              features={features}
              selectedIds={selections[tour.tourId] ?? []}
              defaultFeatureIds={defaultFeatureIds}
              languageCode={activeLanguage}
              disabled={submitting}
              onToggle={(featureId, checked) => setFeatureChecked(tour.tourId, featureId, checked)}
              onResetToDefault={() => resetTourToDefault(tour.tourId)}
            />
          ))
        )}
      </CardContent>
      <CardFooter className="justify-end gap-2 border-t border-zinc-100 pt-4">
        <Button type="button" variant="ghost" disabled={!isDirty || submitting} onClick={resetAll}>
          {t('common.reset', { defaultValue: 'Reset' })}
        </Button>
        <Button
          type="button"
          disabled={!isDirty || submitting}
          onClick={() => void onSave(payload)}
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </CardFooter>
    </Card>
  )
}
